/**
 * Doctor Console config API.
 *
 * Persists per-doctor overrides via REST API. Manager UI and DoctorConsole
 * both call these functions.
 */

import axios from "../../../login/Axios-interceptor";
import { normalizeDoctorId } from "../normalizeDoctorId";

export { normalizeDoctorId };

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
const API_BASE = `${REACT_APP_BACKEND_ROUTE_URL}doctor_console_config/`;

const getAuthHeaders = () => {
  const token = sessionStorage.getItem("accessToken") || "";
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    "App-Mode":
      sessionStorage.getItem("APP_MODE") ||
      sessionStorage.getItem("appMode") ||
      "",
    "Org-Code":
      sessionStorage.getItem("ORG_CODE") ||
      sessionStorage.getItem("orgCode") ||
      "",
    Username:
      sessionStorage.getItem("userName") ||
      sessionStorage.getItem("username") ||
      "",
    "User-Access-Key":
      sessionStorage.getItem("USER_ACCESS_KEY") ||
      sessionStorage.getItem("accessKey") ||
      "",
  };
};

const unwrapResponse = (response) => {
  const data = response?.data;
  if (data?.status === "Failed") {
    const err = new Error(data.message || "Doctor console config request failed");
    err.errors = data.errors;
    err.status = response?.status;
    throw err;
  }
  return data;
};

const ensureSchemaVersion = (config = {}) => ({
  ...config,
  schemaVersion: config?.schemaVersion || "1.0.0",
});

export const fetchDoctorConsoleConfig = async (doctorId) => {
  const id = normalizeDoctorId(doctorId);
  const response = await axios.get(`${API_BASE}`, {
    params: { doctor_id: id },
    headers: getAuthHeaders(),
  });
  return unwrapResponse(response);
};

/** Manager editor: load the newest saved version (draft or published). */
export const fetchDoctorConsoleConfigForManager = async (doctorId) => {
  const id = normalizeDoctorId(doctorId);
  const response = await axios.get(`${API_BASE}manager/`, {
    params: { doctor_id: id },
    headers: getAuthHeaders(),
  });
  return unwrapResponse(response);
};

export const fetchDoctorConsoleConfigVersions = async (doctorId) => {
  const id = normalizeDoctorId(doctorId);
  const response = await axios.get(`${API_BASE}versions/`, {
    params: { doctor_id: id },
    headers: getAuthHeaders(),
  });
  return unwrapResponse(response);
};

export const saveDoctorConsoleConfigDraft = async (doctorId, config, label = "Draft") => {
  const id = normalizeDoctorId(doctorId);
  const normalizedConfig = ensureSchemaVersion(config);
  const response = await axios.post(
    `${API_BASE}draft/`,
    { doctor_id: id, label, config: normalizedConfig },
    { headers: getAuthHeaders() },
  );
  return unwrapResponse(response);
};

export const publishDoctorConsoleConfigVersion = async (doctorId, versionId) => {
  const id = normalizeDoctorId(doctorId);
  const response = await axios.post(
    `${API_BASE}publish/`,
    { doctor_id: id, version_id: versionId },
    { headers: getAuthHeaders() },
  );
  return unwrapResponse(response);
};

export const resetDoctorConsoleConfig = async (doctorId) => {
  const id = normalizeDoctorId(doctorId);
  const response = await axios.delete(`${API_BASE}`, {
    params: { doctor_id: id },
    headers: getAuthHeaders(),
  });
  return unwrapResponse(response);
};

export const deleteDoctorConsoleConfigVersion = async (doctorId, versionId) => {
  const id = normalizeDoctorId(doctorId);
  const response = await axios.delete(`${API_BASE}version/`, {
    params: { doctor_id: id, version_id: versionId },
    headers: getAuthHeaders(),
  });
  return unwrapResponse(response);
};
